import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, PiggyBank, ArrowUpCircle, Repeat } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value || 0);

export const MonthlyTopUpDialog = ({ portfolio, isOpen, onClose, onUpdated }) => {
  const [amount, setAmount] = useState("250");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (isOpen && portfolio) {
      setAmount(String(portfolio.monthly_topup_amount || 250));
      setResult(null);
    }
  }, [isOpen, portfolio]);

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const getRecommendation = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/hl-portfolios/${portfolio.id}/monthly-topup`, { amount: value });
      setResult(data);
      toast.success("AI recommendation ready");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get recommendation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="monthly-topup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-emerald-400" />
            Add This Month's Cash
          </DialogTitle>
          <DialogDescription>{portfolio?.name} • Where should this go?</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="topup-amount">Amount to add (£)</Label>
            <Input
              id="topup-amount"
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="monthly-topup-amount-input"
            />
          </div>
          <Button
            onClick={getRecommendation}
            disabled={loading}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            data-testid="monthly-topup-generate-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Asking AI...
              </>
            ) : (
              <>Get AI Recommendation</>
            )}
          </Button>
        </div>

        {result && (
          <div className="space-y-3 py-2" data-testid="monthly-topup-result">
            <div className="flex items-center gap-2">
              <Badge className={result.decision === "TOP_UP" ? "bg-blue-500" : "bg-purple-500"}>
                {result.decision === "TOP_UP" ? (
                  <>
                    <ArrowUpCircle className="w-3 h-3 mr-1" /> Top Up
                  </>
                ) : (
                  <>
                    <Repeat className="w-3 h-3 mr-1" /> Rotate
                  </>
                )}
              </Badge>
              <span className="text-sm font-medium">{result.fund_name}</span>
            </div>
            <p className="text-sm text-muted-foreground">{result.reason}</p>
            <p className="text-xs text-muted-foreground italic">
              This is a recommendation only - place the trade yourself on Hargreaves Lansdown.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
